import React, { useContext, useEffect, useState } from "react";
import { Autocomplete, Avatar, Box, CircularProgress, Stack, TextField } from "@mui/material";
import { useIntl } from "react-intl";

import UserContext from "../../../context/UserContext";
import performRequest from "../../../network/perform-request";
import { tournamentEligibleMembersRoute } from "../../../network/api-routes";
import RevaText from "components/common/RevaText";

const TournamentMemberSelector = ({
  tournamentId,
  value = [],
  onChange,
  maxSelections = 1,
  label,
  excludedMemberIds = [],
}) => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tournamentId) return undefined;

    let active = true;
    const timeout = setTimeout(() => {
      setLoading(true);
      performRequest("GET", tournamentEligibleMembersRoute(tournamentId), { query, per_page: 30 }, true, user)
        .then((response) => {
          if (active) setOptions(response?.data?.data || []);
        })
        .catch(() => {
          if (active) setOptions([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [query, tournamentId, user]);

  return (
    <Autocomplete
      multiple
      filterOptions={(items) => items}
      options={options}
      value={value}
      loading={loading}
      getOptionLabel={(option) => option?.name || ""}
      getOptionDisabled={(option) =>
        !option?.eligible ||
        excludedMemberIds.map(String).includes(String(option?.organization_member_id)) ||
        (value.length >= maxSelections &&
          !value.some(
            (selected) => String(selected?.organization_member_id) === String(option?.organization_member_id),
          ))
      }
      isOptionEqualToValue={(option, selected) =>
        String(option?.organization_member_id) === String(selected?.organization_member_id)
      }
      onInputChange={(_, nextQuery) => setQuery(nextQuery)}
      onChange={(_, selected) => onChange(selected.slice(0, maxSelections))}
      noOptionsText={intl.formatMessage({ id: "No se encontraron miembros disponibles" })}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            label ||
            intl.formatMessage({ id: maxSelections === 1 ? "Seleccionar miembro" : "Seleccionar miembros" })
          }
          helperText={intl.formatMessage(
            { id: "selector.members.selected" },
            { selected: value.length, maximum: maxSelections },
          )}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.organization_member_id}>
          <Stack direction="row" spacing={1.5} alignItems="center" width="100%">
            <Avatar src={option.profile_picture_url} alt={option.name} />
            <Stack minWidth={0}>
              <RevaText>{option.name}</RevaText>
              <RevaText
                variant="caption"
                color={!option.eligible || !option.is_active ? "warning.main" : "text.secondary"}
              >
                {option.eligible
                  ? [
                      option.member_number
                        ? intl.formatMessage({ id: "selector.members.number" }, { number: option.member_number })
                        : null,
                      intl.formatMessage({ id: option.is_active ? "Miembro activo" : "Miembro inactivo" }),
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : option.ineligibility?.message || intl.formatMessage({ id: "No cumple los requisitos del torneo" })}
              </RevaText>
            </Stack>
          </Stack>
        </Box>
      )}
    />
  );
};

export default TournamentMemberSelector;
